import { getCompactDeviceStatus, type DeviceItem, type DeviceModuleItem } from './missionModels';

export type DeviceStatusSnapshot = {
    unit?: {
        state?: string | null;
        activeMode?: string | null;
        manualOverride?: boolean | null;
        updatedAt?: string | null;
    } | null;
    battery?: {
        percent?: number | null;
        voltage?: number | null;
        state?: string | null;
        available?: boolean | null;
        updatedAt?: string | null;
    } | null;
    mission?: {
        missionId?: number | null;
        operationId?: number | null;
        operationMissionId?: number | null;
        state?: string | null;
        errorCode?: string | null;
        message?: string | null;
        waypointIndex?: number | null;
        totalWaypoints?: number | null;
        percentComplete?: number | null;
        updatedAt?: string | null;
    } | null;
    localization?: {
        environment?: string | null;
        mapId?: number | null;
        state?: string | null;
        ready?: boolean | null;
        message?: string | null;
        updatedAt?: string | null;
    } | null;
    modules?: Record<string, {
        moduleKey?: string | null;
        status?: string | null;
        health?: string | null;
        message?: string | null;
        updatedAt?: string | null;
        [key: string]: any;
    }> | null;
    navigationStatus?: string | null;
    explorationStatus?: string | null;
    pose?: Record<string, any> | null;
    location?: Record<string, any> | null;
    telemetry?: Record<string, any> | null;
    errors?: Array<Record<string, any>>;
    activeMission?: Record<string, any> | null;
    stagedMissions?: Array<Record<string, any>>;
    stagedMissionsUpdatedAt?: string | null;
    localInterface?: {
        url?: string | null;
        available?: boolean | null;
        updatedAt?: string | null;
    } | null;
};

const isObject = (value: unknown): value is Record<string, any> => (
    Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

export const getStatusSnapshot = (device?: Pick<DeviceItem, 'statusSnapshot'> | null): DeviceStatusSnapshot | null => (
    isObject(device?.statusSnapshot) ? device?.statusSnapshot as DeviceStatusSnapshot : null
);

export const getBatteryPercent = (snapshot: DeviceStatusSnapshot | null, fallback?: number): number | undefined => {
    const value = snapshot?.battery?.percent;
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Math.max(0, Math.min(100, value));
    }
    if (snapshot?.battery?.available === false) {
        return undefined;
    }
    return typeof fallback === 'number' && Number.isFinite(fallback) ? fallback : undefined;
};

export const getUnitStatus = (device: Pick<DeviceItem, 'currentStatus' | 'statusSnapshot'>): string | null => {
    const snapshot = getStatusSnapshot(device);
    return getCompactDeviceStatus(snapshot?.unit?.state)
        || getCompactDeviceStatus(device.currentStatus)
        || null;
};

const DEVICE_HEARTBEAT_FRESH_MS = 60 * 1000;

export const getHeartbeatAwareDeviceStatus = (
    device?: Pick<DeviceItem, 'currentStatus' | 'statusSnapshot' | 'lastHeartbeat'> | null
): { displayStatus: string; reportedStatus: string | null; stale: boolean } => {
    const rawReportedStatus = device ? getUnitStatus(device) : null;
    const reportedStatus = rawReportedStatus === 'failed' || rawReportedStatus === 'failure'
        ? 'error'
        : rawReportedStatus;
    const heartbeatAt = device?.lastHeartbeat ? new Date(device.lastHeartbeat).getTime() : Number.NaN;
    const stale = !Number.isFinite(heartbeatAt) || Date.now() - heartbeatAt >= DEVICE_HEARTBEAT_FRESH_MS;

    return {
        displayStatus: stale ? 'offline' : reportedStatus || 'online',
        reportedStatus,
        stale,
    };
};

export const getActiveMode = (snapshot: DeviceStatusSnapshot | null): string | null => (
    snapshot?.unit?.activeMode || snapshot?.navigationStatus || snapshot?.explorationStatus || null
);

export const getManualOverride = (snapshot: DeviceStatusSnapshot | null): boolean => (
    snapshot?.unit?.manualOverride === true || (
        Boolean(snapshot?.unit?.activeMode) && snapshot?.unit?.activeMode !== 'autonomous'
    )
);

export const getLocalizationLabel = (snapshot: DeviceStatusSnapshot | null): string | null => {
    const localization = snapshot?.localization;
    if (!localization) return null;
    const environment = localization.environment || 'unknown';
    const state = localization.state || (localization.ready ? 'ready' : 'not ready');
    return `${environment} · ${state}`;
};

export const getMissionProgress = (snapshot: DeviceStatusSnapshot | null): number | null => {
    const value = snapshot?.mission?.percentComplete;
    return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
};

export const getModuleRuntimeRows = (
    snapshot: DeviceStatusSnapshot | null,
    modules?: DeviceModuleItem[] | null
): Array<{ key: string; name: string; status: string; health?: string | null; message?: string | null; updatedAt?: string | null }> => {
    const runtime = isObject(snapshot?.modules) ? snapshot?.modules || {} : {};
    const rows = (modules || []).map((module, index) => {
        const key = module.instanceKey || module.moduleDefinition?.key || module.name || `module-${index + 1}`;
        const item = runtime[key] || (module.moduleDefinition?.key ? runtime[module.moduleDefinition.key] : null) || {};
        return {
            key,
            name: module.name || module.moduleDefinition?.name || key,
            status: item.status || module.status || 'unknown',
            health: item.health || null,
            message: item.message || null,
            updatedAt: item.updatedAt || null,
        };
    });

    Object.entries(runtime).forEach(([key, item]) => {
        if (rows.some((row) => row.key === key)) return;
        rows.push({
            key,
            name: item.moduleKey || key,
            status: item.status || 'unknown',
            health: item.health || null,
            message: item.message || null,
            updatedAt: item.updatedAt || null,
        });
    });

    return rows;
};

export const getLocalInterfaceUrl = (snapshot: DeviceStatusSnapshot | null): string | null => {
    const url = snapshot?.localInterface?.url;
    return typeof url === 'string' && /^https?:\/\//i.test(url.trim()) ? url.trim() : null;
};
